/**
 * ReportsPage — public, indexable browser of the Reports & Research library
 * (SEO expansion 3/9). Everyone sees the FULL list with URL pagination
 * (/reports?page=N, each page canonical). Category chips link to the hub
 * pages; full-text search stays reserved to logged-in users.
 */
import React, { useEffect, useState, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../AuthContext";
import { API } from "../config";
import { FileText, RefreshCw, Search, X, ArrowLeft, ArrowRight, Lock } from "lucide-react";
import ReportCard from "../components/ReportCard";
import ReportsSignupCta from "../components/reports/ReportsSignupCta";
import PublicPageNav from "../components/PublicPageNav";
import PublicPageFooter from "../components/PublicPageFooter";
import { useDarkMode } from "../hooks/useDarkMode";
import { setCanonical, setDescription, setJsonLd, SITE_URL } from "../lib/seo";

const PAGE_SIZE = 24;
const DESCRIPTION = "Curated library of travel and tourism industry reports from WTTC, OECD, UN Tourism, McKinsey, Phocuswright, Booking.com and 150+ sources — each with an original TourismIntel brief: key findings, numbers, methodology and the official link.";

const ReportsPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const page = useMemo(() => {
    const p = parseInt(new URLSearchParams(location.search).get("page"), 10);
    return Number.isFinite(p) && p > 1 ? p : 1;
  }, [location.search]);
  const search = useMemo(() => (new URLSearchParams(location.search).get("q") || "").trim(), [location.search]);

  const [reports, setReports] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchInput, setSearchInput] = useState(search);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [searchLocked, setSearchLocked] = useState(false);
  const [dm] = useDarkMode();

  useEffect(() => {
    axios.get(`${API}/reports/categories`).then((res) => setCategories(res.data.categories || [])).catch(() => {});
  }, []);

  useEffect(() => {
    document.title = page > 1
      ? `Travel & Tourism Industry Reports — Page ${page} | TourismIntel Reports`
      : "Travel & Tourism Industry Reports & Research — briefed | TourismIntel";
    const c1 = setDescription(DESCRIPTION);
    setCanonical(`/reports${page > 1 ? `?page=${page}` : ""}`);
    setLoading(true);
    const params = { page, limit: PAGE_SIZE };
    if (search) params.search = search;
    axios.get(`${API}/reports`, { params })
      .then((res) => {
        setReports(res.data.reports || []);
        setTotal(res.data.total || 0);
        setHasMore(!!res.data.has_more);
        setSearchLocked(!!res.data.search_locked);
      })
      .catch(() => setReports([]))
      .finally(() => setLoading(false));
    return c1;
  }, [page, search]);

  useEffect(() => {
    if (!reports.length) return undefined;
    return setJsonLd("reports-jsonld", {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "@id": `${SITE_URL}/reports${page > 1 ? `?page=${page}` : ""}`,
      name: "Travel & Tourism Industry Reports & Research",
      description: DESCRIPTION,
      inLanguage: "en",
      mainEntity: {
        "@type": "ItemList",
        itemListOrder: "https://schema.org/ItemListOrderDescending",
        numberOfItems: total,
        itemListElement: reports.map((r, i) => ({
          "@type": "ListItem", position: (page - 1) * PAGE_SIZE + i + 1,
          url: `${SITE_URL}/reports/${r.slug}`, name: r.clean_title || r.title,
        })),
      },
    });
  }, [reports, page, total]);

  const submitSearch = (e) => {
    e.preventDefault();
    if (!user) return;
    navigate(searchInput.trim() ? `/reports?q=${encodeURIComponent(searchInput.trim())}` : "/reports");
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pageHref = (p) => (p <= 1 ? "/reports" : `/reports?page=${p}`) + (search ? `${p <= 1 ? "?" : "&"}q=${encodeURIComponent(search)}` : "");
  const c = dm
    ? { text: "text-white", sub: "text-slate-400", muted: "text-slate-500", chip: "bg-[#1a2122] text-slate-300 hover:bg-[#222b2c]", btn: "bg-[#1a2122] text-slate-300 hover:bg-[#222b2c]" }
    : { text: "text-[#191c1d]", sub: "text-[#3b494c]", muted: "text-[#6b7a7d]", chip: "bg-white text-[#3b494c] border border-black/10 hover:bg-black/5", btn: "bg-white border border-black/10 text-[#3b494c] hover:bg-black/5" };

  return (
    <div className={`min-h-screen ${dm ? "bg-[#0c1011] text-slate-200" : "bg-[#f8f9fa] text-[#191c1d]"}`}>
      <PublicPageNav active="reports" />

      <section className="max-w-6xl mx-auto px-4 md:px-8 pt-8 md:pt-12 pb-6" data-testid="reports-page">
        <Link to={user ? "/feed" : "/"} className={`inline-flex items-center gap-1 text-sm mb-4 ${c.muted} hover:underline`}>
          <ArrowLeft className="w-3 h-3" /> Back
        </Link>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="max-w-3xl">
            <h1 className={`font-serif text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 leading-tight ${c.text}`}>
              Reports &amp; Research
            </h1>
            <p className={`text-sm md:text-base mb-3 ${c.sub}`} data-testid="reports-intro">
              The travel industry publishes hundreds of reports a year — official statistics from WTTC, OECD and UN Tourism,
              consumer research from Booking.com, Expedia and Airbnb, analyst studies from Phocuswright, Skift and McKinsey,
              destination barometers from ETC and national tourism boards. This library collects them in one place, sorted
              by publication date.
            </p>
            <p className={`text-sm md:text-base mb-3 ${c.sub}`}>
              Every report gets an <strong>original TourismIntel brief</strong>: the findings at a glance, the key numbers
              exactly as published, the methodology and its limits, and what it means for destinations, hotels and travel
              tech — with the official source always one click away. The library is curated from 150+ sources and updated
              weekly; new reports are briefed within a day of discovery.
            </p>
            <p className={`text-xs ${c.muted}`}>
              Browse by category below, or open any report for its full brief and Markdown version.
            </p>
          </div>
          <div className={`text-right ${c.muted}`}>
            <div className={`text-3xl font-serif font-bold ${c.text}`} data-testid="reports-total">{total}</div>
            <div className="text-xs uppercase tracking-wider">Reports</div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 md:px-8 pb-4">
        <form onSubmit={submitSearch} className="mb-4">
          <div className={`relative flex items-center rounded-lg border ${dm ? "bg-[#10171a] border-white/8" : "bg-white border-black/8"} ${!user ? "opacity-70" : ""}`}>
            {user ? <Search className={`absolute left-3 w-4 h-4 ${c.muted}`} /> : <Lock className={`absolute left-3 w-4 h-4 ${c.muted}`} />}
            <input
              data-testid="reports-search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              disabled={!user}
              placeholder={user ? "Search reports by title, organisation, or focus…" : "Full-text search is for members — create a free account"}
              className={`w-full pl-10 pr-10 py-2.5 text-sm bg-transparent outline-none ${dm ? "text-white placeholder:text-slate-500" : "text-[#191c1d] placeholder:text-[#6b7a7d]"} ${!user ? "cursor-not-allowed" : ""}`}
            />
            {search && user && (
              <button type="button" onClick={() => { setSearchInput(""); navigate("/reports"); }} data-testid="reports-search-clear"
                className={`absolute right-2 p-1 rounded ${dm ? "hover:bg-white/5" : "hover:bg-black/5"}`}>
                <X className="w-4 h-4" />
              </button>
            )}
            {!user && (
              <Link to="/auth" data-testid="reports-search-signup" className="absolute right-2 text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: "#006874", color: "#fff" }}>
                Sign up
              </Link>
            )}
          </div>
          {searchLocked && <p className={`text-xs mt-1 ${c.muted}`} data-testid="reports-search-locked">Search is available to members — showing the full list instead.</p>}
        </form>

        <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" data-testid="category-chips">
          <span data-testid="category-chip-all" className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold ${dm ? "bg-emerald-400 text-emerald-950" : "bg-emerald-600 text-white"}`}>
            All <span className="opacity-60">({total})</span>
          </span>
          {categories.map((cat) => (
            <Link key={cat.slug} to={`/reports/category/${cat.slug}`} data-testid={`category-chip-${cat.slug}`}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${c.chip}`}>
              {cat.label} <span className="opacity-60">({cat.count})</span>
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 md:px-8 pb-16">
        {loading ? (
          <div className="flex justify-center py-16"><RefreshCw className={`w-6 h-6 animate-spin ${dm ? "text-emerald-400" : "text-emerald-600"}`} /></div>
        ) : reports.length === 0 ? (
          <div className={`text-center py-16 ${c.muted}`} data-testid="reports-empty">
            <FileText className="w-10 h-10 mx-auto mb-3 opacity-30" />
            {page > 1 ? <>No reports on this page — <Link to="/reports" className="underline">back to the latest</Link>.</> : "No reports match your search."}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="reports-grid">
              {reports.slice(0, 2).map((r) => <ReportCard key={r.id} report={r} darkMode={dm} />)}
            </div>
            {page === 1 && <div className="my-6"><ReportsSignupCta dm={dm} total={total} testid="reports-signup-cta-top" /></div>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-testid="reports-grid-rest">
              {reports.slice(2).map((r) => <ReportCard key={r.id} report={r} darkMode={dm} />)}
            </div>

            {total > PAGE_SIZE && (
              <nav className="mt-10 flex justify-center items-center gap-2" data-testid="reports-pagination" aria-label="Pagination">
                {page > 1 ? (
                  <Link to={pageHref(page - 1)} data-testid="reports-pagination-prev" className={`px-4 py-2 rounded-lg text-sm font-semibold ${c.btn}`}>
                    <span className="inline-flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" /> Newer</span>
                  </Link>
                ) : <span className={`px-4 py-2 rounded-lg text-sm font-semibold opacity-40 ${c.btn}`}>Newer</span>}
                <div className={`px-4 py-2 text-sm ${c.sub}`} data-testid="reports-pagination-label">Page {page} of {totalPages}</div>
                {hasMore ? (
                  <Link to={pageHref(page + 1)} data-testid="reports-pagination-next" className={`px-4 py-2 rounded-lg text-sm font-semibold ${c.btn}`}>
                    <span className="inline-flex items-center gap-1">Older <ArrowRight className="w-3.5 h-3.5" /></span>
                  </Link>
                ) : <span className={`px-4 py-2 rounded-lg text-sm font-semibold opacity-40 ${c.btn}`}>Older</span>}
              </nav>
            )}
            <div className="mt-10"><ReportsSignupCta dm={dm} total={total} testid="reports-signup-cta-bottom" /></div>
          </>
        )}
      </section>

      <PublicPageFooter dark={dm} />
    </div>
  );
};

export default ReportsPage;
